import { sortPartialDate } from 'neuro-utils';
import { clinicallyEstablishedPD, probablePD } from './PD';
import {
  checkDiagnosticCriteriaForClinicallyEstablishedMSA,
  checkDiagnosticCriteriaForClinicallyProbableMSA,
  possibleMSA,
  probableMSA,
} from './MSA';
import { probableDLB, possibleDLB } from './DLB';
import { ProbableCBDCanBeDiagnosed, PossibleCBDCanBeDiagnosed } from './CBD';

/**
 * Return latest diagnosis document from documents, based on type(s)
 * @param  {string|string[]} type - Type of diagnosis
 * @param  {IDiagnosis[]} documents - Diagnosis documents
 * @returns {IDiagnosis} Diagnosis document
 */
export const lastDiagnosis = (type: string | Array<string | undefined>, documents: Array<IDiagnosis>): IDiagnosis => {
  const arr = (typeof type === 'string' ? [type] : type) as Array<string | undefined>;
  return (
    documents?.filter(d => arr.includes(d.diagnosis)).sort((n1, n2) => sortPartialDate(n2.date, n1.date))?.[0] || {}
  );
};

/**
 * Determines the diagnostic criteria result for a given document in priority order.
 * For types that support it, checks in "waterfall" fashion: clinicallyEstablished → probable → possible → no → unknown → undefined.
 * For others, the highest available tier is used.
 *
 * @param {IPD | IMSA | IMSA2022 | IDLB | ICBD} d - Diagnostic criteria document
 * @param {'pd' | 'msa' | 'dlb' | 'cbd'} type - Diagnostic criteria type
 * @returns {'clinicallyEstablished' | 'probable' | 'possible' | 'no' | 'unknown' | undefined} The first matching result, or `undefined` if type not supported.
 */
export const clinicallyEstablishedOrProbableOrPossible = (
  d: IPD | IMSA | IMSA2022 | IDLB | ICBD,
  type: string,
): 'clinicallyEstablished' | 'probable' | 'possible' | 'no' | 'unknown' | undefined => {
  switch (type) {
    case 'pd':
      if (clinicallyEstablishedPD(d) === 'yes') return 'clinicallyEstablished';
      if (probablePD(d) === 'yes') return 'probable';
      if (clinicallyEstablishedPD(d) === 'no' || probablePD(d) === 'no') return 'no';
      return 'unknown';
    case 'msa':
      const version = !('version' in d) || !d.version || d.version === '2008' ? '2008' : '2022';
      if (version === '2008') {
        if (probableMSA(d as IMSA) === 'yes') return 'probable';
        if (possibleMSA(d as IMSA) === 'yes') return 'possible';
        if (probableMSA(d as IMSA) === 'no' || possibleMSA(d as IMSA) === 'no') return 'no';
        return 'unknown';
      }
      if (checkDiagnosticCriteriaForClinicallyEstablishedMSA(d as IMSA2022) === 'yes') return 'clinicallyEstablished';
      if (checkDiagnosticCriteriaForClinicallyProbableMSA(d as IMSA2022) === 'yes') return 'probable';
      if (
        checkDiagnosticCriteriaForClinicallyEstablishedMSA(d as IMSA2022) === 'no' ||
        checkDiagnosticCriteriaForClinicallyProbableMSA(d as IMSA2022) === 'no'
      )
        return 'no';
      return 'unknown';
    case 'dlb':
      if (probableDLB(d) === 'yes') return 'probable';
      if (possibleDLB(d) === 'yes') return 'possible';
      if (probableDLB(d) === 'no' || possibleDLB(d) === 'no') return 'no';
      return 'unknown';
    case 'cbd':
      if (ProbableCBDCanBeDiagnosed(d) === 'yes') return 'probable';
      if (PossibleCBDCanBeDiagnosed(d) === 'yes') return 'possible';
      if (ProbableCBDCanBeDiagnosed(d) === 'no' || PossibleCBDCanBeDiagnosed(d) === 'no') return 'no';
      return 'unknown';
    default:
      return undefined;
  }
};
